import { inject, Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { classifyAuthError } from '../error-handling/auth-error-handler';
import { AuthSession } from '../models/auth.model';
import { AuthStore } from '../stores/auth-store';

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly _authStore = inject(AuthStore);

  private _refreshInFlight$: Observable<string> | null = null;

  getValidToken$(): Observable<string> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    return this._fetchSession$(false).pipe(
      map((session) => {
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('No access token available');
        }
        return token;
      })
    );
  }

  forceRefresh$(): Observable<string> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    this._authStore.setRefreshInProgress(true);
    this._authStore.setTokenStatus('refreshing');

    this._refreshInFlight$ = this._fetchSession$(true).pipe(
      map((session) => {
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('Token refresh failed - no token returned');
        }
        return token;
      }),
      tap(() => {
        this._authStore.setTokenStatus('valid');
      }),
      catchError((error) => {
        const authError = classifyAuthError(error);
        this._authStore.setLastRefreshError(authError);
        this._authStore.setTokenStatus('invalid');
        return throwError(() => authError);
      }),
      finalize(() => {
        this._refreshInFlight$ = null;
        this._authStore.setRefreshInProgress(false);
      }),
      shareReplay(1)
    );

    return this._refreshInFlight$;
  }

  getAccessToken(): string | null {
    const session = this._authStore.getSessionOrNull();
    return session?.tokens?.accessToken?.toString() ?? null;
  }

  isAuthenticated$(): Observable<boolean> {
    return this._fetchSession$(false).pipe(
      map(
        (session) => session.isAuthenticated && !!session.tokens?.accessToken
      ),
      catchError(() => of(false))
    );
  }

  clearPendingRefresh(): void {
    this._refreshInFlight$ = null;
  }

  private _fetchSession$(forceRefresh: boolean): Observable<AuthSession> {
    return from(fetchAuthSession({ forceRefresh })).pipe(
      map((cognitoSession) => {
        const session: AuthSession = {
          tokens: cognitoSession.tokens ?? null,
          isAuthenticated: !!cognitoSession.tokens?.accessToken,
          lastFetched: Date.now(),
        };

        this._authStore.updateSession(session);

        return session;
      })
    );
  }
}
